<app-logo-area
  [breadcrumbs]="[{'display': 'Content Platform', commands: null}, {'display': 'Platform monitoring', commands: null}]"></app-logo-area>
<main>
  <app-headline-section [title]="'Platform monitoring'">
    <button type="button" class="btn cl-color-sea" (click)="dismiss()"><span>To my work</span></button>
  </app-headline-section>

  @if (noAccess()) {
    <!-- No organization of this user grants monitoring: say so and ask nothing (VTK-3577). -->
    <section class="cl-section cl-section-regular" data-monitoring-no-access>
      <div class="container">
        <div class="row">
          <div class="col-12">
            <p class="cl-alert-locked" role="alert">None of your organizations grants platform monitoring.</p>
          </div>
        </div>
      </div>
    </section>
  } @else {

  <!-- ============================================================ window and freshness -->
  <!-- cl-form is required, not decorative: @castlabs/ui scopes its form-control styling to
       `.cl-form .form-select` / `.cl-form .input-group-text`, so without an ancestor carrying it the
       select falls back to plain Bootstrap. -->
  <section class="cl-section cl-section-regular cl-form">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-12 col-md-5">
          <div class="input-group">
            <span class="input-group-text">Interval</span>
            <select class="form-select form-control"
                    aria-label="Reporting window"
                    (change)="selectWindow($any($event.target).value)">
              @for (option of windows; track option.value) {
                <option [value]="option.value" [selected]="option.value === window()">{{ option.label }}</option>
              }
            </select>
          </div>
        </div>
        <div class="col-12 col-md-7 cl-text-end">
          <p class="cl-p-small cl-font-monospace">window <app-date [value]="windowStart()" empty="—" />@if (windowStart()) { → now}</p>
          <p class="cl-p-small">{{ updatedLabel() }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ============================================================ headline tiles -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">
        @for (tile of tiles(); track tile.name) {
          <div class="col-12 col-md-6 col-xl-3">
            <!-- Red outline marks the card that needs looking at; the badge carries the precise
                 severity, because red and night are the only outline variants the library ships. -->
            <div class="card cl-card-max" [class.cl-color-red-outline]="tile.badge === 'ELEVATED'">
              <div class="card-header">
                {{ tile.name }}
                <span class="cl-badge cl-badge-sm" [class]="tile.badgeClass">{{ tile.badge }}</span>
              </div>
              <div class="card-body">
                <h2 class="cl-font-monospace">{{ tile.rateText }}</h2>
                <p class="cl-p-small">{{ tile.sub }}</p>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </section>

  <!-- ============================================================ portal logins -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h3>Portal logins</h3>

          @if (logins(); as rows) {
            <p class="cl-p-small">
              <strong>{{ loginsToFix().length }} of {{ rows.length }} logins are expired.</strong>
            </p>

            @if (loginErrors()['SIGNIANT']; as message) {
              <p class="cl-alert-danger" role="alert">
                Signiant logins could not be checked: {{ message }}. They are left out of the count.
              </p>
            }
            @if (loginErrors()['ASPERA']; as message) {
              <p class="cl-alert-danger" role="alert">
                Aspera logins could not be checked: {{ message }}. They are left out of the count.
              </p>
            }

            <div class="cl-table-scroll">
              <table class="cl-table-data">
                <thead>
                <tr>
                  <th class="cl-table-head">Service</th>
                  <th class="cl-table-head">Login</th>
                  <th class="cl-table-head">State</th>
                  <th class="cl-table-head cl-th-links"></th>
                </tr>
                </thead>
                <tbody>
                  @for (row of loginsToFix(); track row.id) {
                    <tr>
                      <td>{{ row.service }}</td>
                      <td>{{ row.name }}</td>
                      <td>{{ row.state }}</td>
                      <td class="cl-td-links" width="1%">
                        <a class="cl-a-btn" role="button" [routerLink]="row.loginLink">Log in</a>
                      </td>
                    </tr>
                  } @empty {
                    <tr class="cl-tr-no-data">
                      <td colspan="100">Every login is logged in.</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <p class="cl-p-small">
              Signiant logins are checked live. Aspera shows only whether a login is stored, so a login that has
              since expired at the share still reads as logged in. Checked every 5 minutes.
            </p>
          } @else {
            <p class="cl-p-small">Checking logins…</p>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- ============================================================ processes, one section each -->
  @for (api of apis; track api) {
    <section class="cl-section cl-section-regular">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <h3>{{ apiLabel(api) }}</h3>

            <p class="cl-p-small">
              @for (counter of counters(api); track counter.label) {
                <span>{{ counter.label }} </span>
                <strong class="cl-font-monospace" [style.color]="counter.color">{{ counter.value }}</strong>
                @if (!$last) { <span> &middot; </span> }
              }
            </p>

            @if (errorFor(keyFor(api)); as message) {
              <p class="cl-alert-danger" role="alert">
                {{ apiLabel(api) }} failed to load: {{ message }}
                <a class="cl-a-btn" role="button" (click)="retry()">Retry</a>
              </p>
            }

            @if (staleCount(api) > 0) {
              <p class="cl-alert-warning" role="alert">
                {{ staleCount(api) }} processes are in progress but older than the staleness threshold.
                @if (api === StatsApi.Repository) {
                  The repository API does not reflect an aborted Step Functions execution back into its
                  process table, so aborted processes stay in progress there permanently — treat this as
                  stalled records from a known upstream gap, not a queue.
                }
                They are excluded from the failure rate.
              </p>
            }

            @if (isEmpty(api)) {
              <p class="cl-alert-suggestion">
                No {{ apiLabel(api).toLowerCase() }} in this window. Activity in this environment is
                sporadic — try a wider window before concluding nothing is running.
                @if (window() !== windows[2].value) {
                  <a class="cl-a-btn" role="button" (click)="widenToSevenDays()">Show last 7 days</a>
                }
              </p>
            }

            @if (showTable(api)) {
              <p class="cl-p-small">
                <span>Sort </span>
                @for (option of sorts; track option.key) {
                  <a class="cl-a-btn"
                     role="button"
                     [class.cl-a-primary]="option.key === sortKey(api)"
                     (click)="setSort(api, option.key)">{{ option.label }}</a>
                }
              </p>

              <div class="cl-table-scroll">
                <table class="cl-table-data">
                  <thead>
                  <tr>
                    <th class="cl-table-head">Operation</th>
                    <th class="cl-table-head">Volume</th>
                    <th class="cl-table-head cl-th-currency">Started</th>
                    <th class="cl-table-head cl-th-currency">Failed</th>
                    <th class="cl-table-head cl-th-currency">Failure rate</th>
                    <th class="cl-table-head cl-th-currency">Avg duration</th>
                    <th class="cl-table-head cl-th-links"></th>
                  </tr>
                  </thead>
                  <tbody>
                    @for (row of rows(api); track row.action) {
                      <tr>
                        <td class="cl-td-code">{{ row.action }}</td>
                        <td>
                          <!-- Scaled against the busiest operation, so the bar shows this row's share of
                               the section's work rather than its own internal split. The fill is set per
                               segment because no library class paints a surface in a CI hue; the values
                               come from the library's own SCSS (see the component stylesheet). -->
                          <div class="progress progress-stacked"
                               [attr.title]="row.started + ' started, ' + row.failed + ' failed'">
                            <div class="progress-bar"
                                 style="background: var(--cl-color-ci-clay)"
                                 [style.width.%]="row.volPct"></div>
                            <div class="progress-bar"
                                 style="background: var(--cl-color-ci-brick)"
                                 [style.width.%]="row.failPct"></div>
                          </div>
                        </td>
                        <td class="cl-td-currency">{{ row.started }}</td>
                        <td class="cl-td-currency" [style.color]="row.failedColor">{{ row.failed }}</td>
                        <td class="cl-td-currency">
                          <span [style.color]="row.rateColor">{{ row.rateText }}</span>
                          <br><small>{{ row.denomText }}</small>
                        </td>
                        <td class="cl-td-currency">{{ row.avgText }}</td>
                        <td class="cl-td-links" width="1%">
                          @if (row.hasFailures) {
                            <a class="cl-a-btn" role="button" (click)="viewFailures(api, row.action)">View {{ row.failed }} failures</a>
                          }
                        </td>
                      </tr>
                    } @empty {
                      <tr class="cl-tr-no-data">
                        <td colspan="100">No processes in this window.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else if (loading() && !statsFor(api)) {
              <!-- The table structure stays visible while loading, never replaced by a bare spinner. -->
              <div class="cl-table-scroll">
                <table class="cl-table-data">
                  <thead>
                  <tr>
                    <th class="cl-table-head">Operation</th>
                    <th class="cl-table-head">Volume</th>
                    <th class="cl-table-head cl-th-currency">Started</th>
                    <th class="cl-table-head cl-th-currency">Failed</th>
                    <th class="cl-table-head cl-th-currency">Failure rate</th>
                    <th class="cl-table-head cl-th-currency">Avg duration</th>
                    <th class="cl-table-head cl-th-links"></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr class="cl-tr-no-data">
                    <td colspan="100">
                      <div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            }

            <!-- Failure drill-down: replaces the table rather than nesting inside it. -->
            @if (focusedAction(api); as action) {
              <div class="card">
                <div class="card-header">
                  <a class="cl-a-btn" role="button" (click)="clearFocus(api)">&larr; All operations</a>
                  <span class="cl-font-monospace">{{ action }}</span>
                  <small> {{ focusSummary(api) }}</small>
                </div>
                <div class="card-body">
                  <div class="cl-table-scroll">
                    <table class="cl-table-data">
                      <thead>
                      <tr>
                        <th class="cl-table-head">Process id</th>
                        <th class="cl-table-head">Message</th>
                        <th class="cl-table-head">Started</th>
                        <th class="cl-table-head cl-th-currency">Ran for</th>
                      </tr>
                      </thead>
                      <tbody>
                        @if (failuresLoading() && !failures()) {
                          <tr class="cl-tr-no-data">
                            <td colspan="100">
                              <div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div>
                            </td>
                          </tr>
                        } @else if (!failures()?.length) {
                          <tr class="cl-tr-no-data">
                            <td colspan="100">No failure detail available.</td>
                          </tr>
                        } @else {
                          @for (failure of failures(); track failure.id) {
                            <tr>
                              <!-- The app's existing process link: builds the Step Functions console URL
                                   for the right state machine, and only for staff. -->
                              <td class="cl-td-id">
                                <app-process-link [processId]="failure.id"
                                                  [type]="api === StatsApi.Repository ? 'cr' : 'wf'"></app-process-link>
                              </td>
                              <td>{{ failure.message || 'No message returned by the process' }}</td>
                              <td class="cl-td-time"><app-date [value]="failure.start_date" /></td>
                              <td class="cl-td-currency">{{ failureDuration(failure) }}</td>
                            </tr>
                          }
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="card-footer">
                  <small>Showing {{ failures()?.length || 0 }} failures &middot; newest first</small>
                  @if (failuresNextToken()) {
                    <a class="cl-a-btn" role="button" (click)="loadMoreFailures(api)">
                      {{ failuresLoading() ? 'Loading…' : 'Load 50 more' }}
                    </a>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </section>
  }

  <!-- ============================================================ publication methods -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h3>Publication methods</h3>

          <p class="cl-p-small">
            <!-- The list is discovered from the deployed publish state machines at query time, so a
                 method missing here is genuinely not deployed, not merely not configured for
                 monitoring. -->
            <span class="cl-badge cl-badge-sm cl-color-clay">AUTODISCOVERED</span>
            @if (publish(); as stats) {
              <span> {{ stats.methods.length }} configured methods &middot; </span>
              <app-date [value]="stats.window_start" /> → <app-date [value]="stats.window_end" />
            }
          </p>

          @if (errorFor('PUBLISH'); as message) {
            <p class="cl-alert-danger" role="alert">
              Publication method statistics could not be loaded: {{ message }}
              <a class="cl-a-btn" role="button" (click)="retry()">Retry</a>
            </p>
          }

          @if (publishIsEmpty()) {
            <p class="cl-alert-suggestion">
              No publishes in this window. Deliveries are driven by orders, so quiet periods are
              normal — try a wider window before concluding a method is idle.
              @if (window() !== windows[2].value) {
                <a class="cl-a-btn" role="button" (click)="widenToSevenDays()">Show last 7 days</a>
              }
            </p>
          }

          @if (showPublishTable()) {
            <div class="cl-table-scroll">
              <table class="cl-table-data">
                <thead>
                <tr>
                  <th class="cl-table-head">Method</th>
                  <th class="cl-table-head">Volume</th>
                  <th class="cl-table-head cl-th-currency">Started</th>
                  <th class="cl-table-head cl-th-currency">Failed</th>
                  <th class="cl-table-head cl-th-currency">Failure rate</th>
                  <th class="cl-table-head cl-th-currency">Avg duration</th>
                  <th class="cl-table-head cl-th-links"></th>
                </tr>
                </thead>
                <tbody>
                  @for (row of publishRows(); track row.stateMachine) {
                    <tr>
                      <td>
                        <span class="cl-font-monospace">{{ row.action }}</span>
                        <br><small class="cl-font-monospace">{{ row.stateMachine }}</small>
                      </td>
                      <td>
                        <div class="progress progress-stacked"
                             [attr.title]="row.started + ' started, ' + row.failed + ' failed'">
                          <div class="progress-bar"
                               style="background: var(--cl-color-ci-clay)"
                               [style.width.%]="row.volPct"></div>
                          <div class="progress-bar"
                               style="background: var(--cl-color-ci-brick)"
                               [style.width.%]="row.failPct"></div>
                        </div>
                      </td>
                      <td class="cl-td-currency">{{ row.started }}</td>
                      <td class="cl-td-currency" [style.color]="row.failedColor">{{ row.failed }}</td>
                      <td class="cl-td-currency">
                        <span [style.color]="row.rateColor">{{ row.rateText }}</span>
                        <br><small>{{ row.denomText }}</small>
                      </td>
                      <td class="cl-td-currency">{{ row.avgText }}</td>
                      <td class="cl-td-links" width="1%">
                        @if (row.hasFailures) {
                          <a class="cl-a-btn" role="button" (click)="viewPublishFailures(row.stateMachine)">View {{ row.failed }} failures</a>
                        }
                      </td>
                    </tr>
                  } @empty {
                    <tr class="cl-tr-no-data">
                      <td colspan="100">No publication methods discovered.</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else if (loading() && !publish()) {
            <!-- The table structure stays visible while loading, never replaced by a bare spinner. -->
            <div class="cl-table-scroll">
              <table class="cl-table-data">
                <thead>
                <tr>
                  <th class="cl-table-head">Method</th>
                  <th class="cl-table-head">Volume</th>
                  <th class="cl-table-head cl-th-currency">Started</th>
                  <th class="cl-table-head cl-th-currency">Failed</th>
                  <th class="cl-table-head cl-th-currency">Failure rate</th>
                  <th class="cl-table-head cl-th-currency">Avg duration</th>
                  <th class="cl-table-head cl-th-links"></th>
                </tr>
                </thead>
                <tbody>
                <tr class="cl-tr-no-data">
                  <td colspan="100">
                    <div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          }

          <!-- Failure drill-down: replaces the table rather than nesting inside it. -->
          @if (publishFocus(); as machine) {
            <div class="card">
              <div class="card-header">
                <a class="cl-a-btn" role="button" (click)="clearPublishFocus()">&larr; All methods</a>
                <span class="cl-font-monospace">{{ machine }}</span>
                <small> {{ publishFocusSummary() }}</small>
              </div>
              <div class="card-body">
                <div class="cl-table-scroll">
                  <table class="cl-table-data">
                    <thead>
                    <tr>
                      <th class="cl-table-head">Execution</th>
                      <th class="cl-table-head">Started</th>
                      <th class="cl-table-head cl-th-currency">Ran for</th>
                    </tr>
                    </thead>
                    <tbody>
                      @if (publishFailuresLoading() && !publishFailures()) {
                        <tr class="cl-tr-no-data">
                          <td colspan="100">
                            <div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div>
                          </td>
                        </tr>
                      } @else if (!publishFailures()?.length) {
                        <tr class="cl-tr-no-data">
                          <td colspan="100">No failure detail available.</td>
                        </tr>
                      } @else {
                        @for (failure of publishFailures(); track failure.execution) {
                          <tr>
                            <td class="cl-td-id">{{ failure.execution }}</td>
                            <td class="cl-td-time"><app-date [value]="failure.start_date" /></td>
                            <td class="cl-td-currency">{{ publishFailureDuration(failure) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="card-footer">
                <small>Showing {{ publishFailures()?.length || 0 }} failures &middot; newest first</small>
                @if (publishFailuresNextToken()) {
                  <a class="cl-a-btn" role="button" (click)="loadMorePublishFailures()">
                    {{ publishFailuresLoading() ? 'Loading…' : 'Load 50 more' }}
                  </a>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- ============================================================ VTK -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h3>VTK jobs · all organizations</h3>

          <p class="cl-p-small">
            <span class="cl-badge cl-badge-sm cl-color-clay">5 MIN SNAPSHOT</span>
            @if (vtk()?.collected_at) {
              <span class="cl-font-monospace"> collected <app-date [value]="vtk().collected_at" /></span>
            } @else {
              <span> never collected</span>
            }
            <!-- The window these figures cover, read off the VTK response rather than the header, so a
                 panel left behind by a failed refetch cannot pass for the selected period. -->
            @if (vtkWindow(); as range) {
              <span> &middot; </span>
              <app-date [value]="range.start" /> → <app-date [value]="range.end" />
            }
          </p>

          @if (errorFor('VTK'); as message) {
            <p class="cl-alert-danger" role="alert">
              VTK snapshot could not be loaded: {{ message }}. Repository and workflow data above is
              unaffected.
              <a class="cl-a-btn" role="button" (click)="retry()">Retry</a>
            </p>
          }

          @if (vtkIsEmpty()) {
            <p class="cl-alert-suggestion">
              No encoding jobs in this window. VTK activity in this environment is sporadic — a wider
              window usually has jobs.
              @if (window() !== windows[2].value) {
                <a class="cl-a-btn" role="button" (click)="widenToSevenDays()">Show last 7 days</a>
              }
            </p>
          }

          @if (vtk(); as stats) {
            @if (stats.total > 0 && !errorFor('VTK')) {
              <div class="row align-items-end">
                <div class="col-12 col-md-3">
                  <p class="cl-p-label">Jobs in window</p>
                  <h2 class="cl-font-monospace">{{ stats.total }}</h2>
                </div>
                <div class="col-12 col-md-9">
                  <div class="progress progress-stacked"
                       role="img"
                       [attr.aria-label]="'Job status distribution across ' + stats.total + ' jobs'">
                    @for (segment of vtkSegments(); track segment.label) {
                      <div class="progress-bar"
                           [style.background]="segment.color"
                           [style.width.%]="segment.pct"
                           [attr.title]="segment.label + ': ' + segment.count"></div>
                    }
                  </div>
                  <!-- Legend, so status is never carried by colour alone. -->
                  <p class="cl-p-small">
                    @for (segment of vtkSegments(); track segment.label) {
                      <span class="cl-badge cl-badge-sm" [style.background]="segment.color">{{ segment.label }}</span>
                      <span class="cl-font-monospace"> {{ segment.count }}</span>
                      @if (!$last) { <span> &middot; </span> }
                    }
                  </p>
                </div>
              </div>

              <div class="cl-table-scroll">
                <table class="cl-table-data">
                  <thead>
                  <tr>
                    <th class="cl-table-head">Organization</th>
                    <th class="cl-table-head">Status mix</th>
                    <th class="cl-table-head cl-th-currency">Failed</th>
                    <th class="cl-table-head cl-th-currency">Total</th>
                    <th class="cl-table-head cl-th-links"></th>
                  </tr>
                  </thead>
                  <tbody>
                    @for (org of vtkOrganizations(); track org.urn) {
                      <tr>
                        <td>
                          {{ org.name }}
                          <br><small class="cl-font-monospace">{{ org.shortUrn }}</small>
                        </td>
                        <td>
                          <div class="progress progress-stacked">
                            @for (segment of org.segments; track segment.label) {
                              <div class="progress-bar"
                                   [style.background]="segment.color"
                                   [style.width.%]="segment.pct"
                                   [attr.title]="segment.label + ': ' + segment.count"></div>
                            }
                          </div>
                        </td>
                        <td class="cl-td-currency" [style.color]="org.failedColor">{{ org.failed }}</td>
                        <td class="cl-td-currency">{{ org.total }}</td>
                        <td class="cl-td-links" width="1%">
                          <!-- The status mix column already shows the breakdown, so the row's action is to
                               go where you can act on it: the VTK app's job list for that org. -->
                          <a class="cl-a-btn cl-a-primary cl-a-external"
                             target="_blank"
                             rel="noopener noreferrer"
                             [href]="vtkAppUrl(org.urn)">Open in VTK</a>
                        </td>
                      </tr>
                    } @empty {
                      <tr class="cl-tr-no-data">
                        <td colspan="100">No organization ran jobs in this window.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              @if (vtkTailNote(); as note) {
                <p class="cl-alert-info">{{ note }}</p>
              }
            }
          }
        </div>
      </div>
    </div>
  </section>

  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h3>Search index</h3>

          <p class="cl-p-small">
            <!-- Read live from OpenSearch, the indexing queue and the scan state machine at query
                 time, so there is no collector interval to allow for: these are current values. -->
            <span class="cl-badge cl-badge-sm cl-color-clay">LIVE</span>
            @if (index(); as stats) {
              <span> alias <span class="cl-font-monospace">{{ stats.alias }}</span></span>
              @if (stats.index) {
                <span> &rarr; <span class="cl-font-monospace">{{ stats.index }}</span></span>
              }
            }
          </p>

          @if (errorFor('INDEX'); as message) {
            <p class="cl-alert-danger" role="alert">
              Search index statistics could not be loaded: {{ message }}
              <a class="cl-a-btn" role="button" (click)="retry()">Retry</a>
            </p>
          }

          @if (index(); as stats) {
            @if (stats.dlq_depth > 0) {
              <p class="cl-alert-danger" role="alert">
                {{ stats.dlq_depth }} indexing messages were dropped and are sitting in a dead-letter
                queue. Those objects exist in S3 and are missing from the index, and nothing retries
                them on its own — they need a redrive.
              </p>
            }

            <div class="cl-table-scroll">
              <table class="cl-table-data cl-tight">
                <thead>
                <tr>
                  <th class="cl-table-head">Measure</th>
                  <th class="cl-table-head cl-th-currency">Value</th>
                  <th class="cl-table-head">What it means</th>
                </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Behind S3</td>
                    <td class="cl-td-currency">
                      @if (stats.lag_seconds !== null && stats.lag_seconds !== undefined) {
                        {{ indexLag() }}
                      } @else {
                        &mdash;
                      }
                    </td>
                    <td class="cl-p-small">Newest indexed change versus now. What a user experiences as
                      "my upload is not showing up".</td>
                  </tr>
                  <tr>
                    <td>Documents</td>
                    <td class="cl-td-currency">{{ stats.documents?.toLocaleString() ?? '—' }}</td>
                    <td class="cl-p-small">{{ stats.files?.toLocaleString() ?? '—' }} files,
                      {{ stats.folders?.toLocaleString() ?? '—' }} folders,
                      {{ stats.deleted?.toLocaleString() ?? '—' }} marked deleted.</td>
                  </tr>
                  <tr [class.cl-highlight]="stats.queue_depth > 0">
                    <td>Queued</td>
                    <td class="cl-td-currency">{{ stats.queue_depth.toLocaleString() }}</td>
                    <td class="cl-p-small">{{ stats.queue_in_flight.toLocaleString() }} in flight, oldest
                      {{ indexQueueAge() }}. Large during a scan, and expected.</td>
                  </tr>
                  <tr [class.cl-highlight]="stats.dlq_depth > 0">
                    <td>Dropped</td>
                    <td class="cl-td-currency">{{ stats.dlq_depth.toLocaleString() }}</td>
                    <td class="cl-p-small">Dead-letter queues combined. Anything above zero is lost
                      indexing work.</td>
                  </tr>
                  <tr>
                    <td>Searches</td>
                    <td class="cl-td-currency">{{ stats.search.requests.toLocaleString() }}</td>
                    <td class="cl-p-small">
                      {{ stats.search.failures.toLocaleString() }} failed
                      @if (stats.search.failure_rate !== null && stats.search.failure_rate !== undefined) {
                        ({{ (stats.search.failure_rate * 100).toFixed(1) }}%)
                      }
                      @if (stats.search.avg_duration_ms) {
                        &middot; {{ (stats.search.avg_duration_ms / 1000).toFixed(1) }} s average
                      }
                    </td>
                  </tr>
                  <!-- A search that succeeds and finds nothing moves no other row in this table. It is
                       what four and a half days of a broken authorization filter looked like. -->
                  <tr [class.cl-highlight]="indexZeroResultSevere()">
                    <td>Found nothing</td>
                    <td class="cl-td-currency" [style.color]="indexZeroResultColor()">
                      @if (stats.search.zero_result_rate !== null && stats.search.zero_result_rate !== undefined) {
                        {{ (stats.search.zero_result_rate * 100).toFixed(0) }}%
                      } @else {
                        &mdash;
                      }
                    </td>
                    <td class="cl-p-small">
                      {{ stats.search.zero_results.toLocaleString() }} of the answered searches returned no
                      results. Some queries legitimately match nothing — a sustained 100% is the shape of a
                      broken filter, not of a quiet day.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            @if (stats.scan; as scan) {
              <p class="cl-p-small">
                <span class="cl-badge cl-badge-sm"
                      [class.cl-color-leaf]="scan.status === 'SUCCEEDED'"
                      [class.cl-color-sea]="scan.status === 'RUNNING'"
                      [class.cl-color-brick]="scan.status !== 'SUCCEEDED' && scan.status !== 'RUNNING'">
                  {{ scan.status }}
                </span>
                <span> last {{ scan.rebuild ? 'rebuild' : 'full scan' }}
                  <span class="cl-font-monospace">{{ scan.execution }}</span>,
                  started <app-date [value]="scan.start_date" /></span>
                @if (scan.stop_date) {
                  <span>, finished <app-date [value]="scan.stop_date" /></span>
                }
              </p>
            } @else {
              <p class="cl-alert-info">
                No full scan on record. Step Functions keeps 90 days, so this also means none has run
                in that time — the index has had no reconciliation against S3.
              </p>
            }
          }
        </div>
      </div>
    </div>
  </section>
  }
</main>
